// @flow

import React from 'react';
import MicRecorder from 'mic-recorder-to-mp3';
import AudioTimer from './audio_timer';
import AudioPlayer from './audio_player';

import { RecordIcon, StopIcon } from '../icons';
import DescriptionTooltip from './tooltips/description_tooltip';
import styled, { css, withTheme } from 'styled-components';
import { colors, ReskinThemes } from '../constants';
import { PulseAnimation } from '../animations';
import { Label, BodyText } from '../typography';

import polyfillUserMedia from '../utils/user_media_polyfill';
import PrimaryPalette from '../../birch/colors';

type Props = {
  recordingData?: string,
  recordingName?: string,
  theme?: { name: string },
};

type State = {
  isBlocked: boolean,
  isRecording: boolean,
  recordingData?: string,
};

const themeSelector = (themeName) => {
  return themeName !== ReskinThemes.BIRCH ? azaleaTheme : birchTheme;
};

const azaleaTheme = {
  buttonColor: colors.darkGreenhouseGreen,
  buttonHoverColor: colors.mediumGreenhouseGreen,
  icon: {
    fillColor: colors.white,
  },
};

const birchTheme = {
  buttonColor: 'transparent',
  buttonHoverColor: 'transparent',
  icon: {
    fillColor: PrimaryPalette.mediumEvergreen,
  },
};

class AudioRecorder extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    polyfillUserMedia();

    // $FlowFixMe
    this.recorder = new MicRecorder({ bitRate: 92 });

    this.state = {
      isBlocked: false,
      isRecording: false,
      recordingData: props.recordingData,
    };
  }

  requestMicrophoneAccess = () => {
    const promise = window.navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });

    // We've polyfilled getUserMedia to always return a promise
    // even if the browser doesn't support it
    return promise.then(
      () => {
        this.setState({ isBlocked: false });
        this.startRecording();
      },
      (error) => {
        this.setState({ isBlocked: true });
        console.error(error);
      }
    );
  };

  startRecording = () => {
    // $FlowFixMe
    this.recorder
      .start()
      .then(() => this.setState({ isRecording: true }))
      .catch((error) => console.error(error));
  };

  stopRecording = () => {
    // $FlowFixMe
    this.recorder
      .stop()
      .getMp3()
      .then(([buffer, blob]) => {
        const file = new File(buffer, 'temp_file.mp3', {
          type: blob.type,
          lastModified: Date.now(),
        });
        return this.convertAudioToDataURL(file);
      })
      .then((recordingData) => {
        this.setState({ recordingData, isRecording: false });
      })
      .catch((error) => console.log(error));
  };

  convertAudioToDataURL = (inputFile: File) => {
    const temporaryFileReader = new FileReader();

    return new Promise<string | ArrayBuffer>((resolve, reject) => {
      temporaryFileReader.onerror = () => {
        temporaryFileReader.abort();
        // $FlowFixMe
        reject(new DOMException('Problem parsing input file.'));
      };

      temporaryFileReader.onload = () => {
        resolve(temporaryFileReader.result);
      };
      temporaryFileReader.readAsDataURL(inputFile);
    });
  };

  onDelete = () => {
    if (!this.state.recordingData) return;

    this.setState({ recordingData: '' });
  };

  disabledTooltip = (children: any) => (
    <DescriptionTooltip
      position="bottom"
      text={
        'You must give this browser access to your microphone to record your name pronunciation'
      }
    >
      {children}
    </DescriptionTooltip>
  );

  isIE = () => {
    const ua = window.navigator.userAgent;
    return ua.indexOf('MSIE ') > -1 || ua.indexOf('Trident/') > -1;
  };

  render() {
    const { recordingData, isRecording, isBlocked } = this.state;
    const Icon = isRecording ? StopIcon : RecordIcon;
    if (this.isIE()) {
      return (
        <BodyText>
          This feature is not available for your browser. Please change browsers
          to access recording.
        </BodyText>
      );
    } else if (recordingData) {
      return (
        <AudioPlayer
          sourceUrl={recordingData}
          compact={false}
          deleteCallback={this.onDelete}
          fileName={this.props.recordingName}
        />
      );
    } else {
      return (
        <Wrapper
          onClick={
            isRecording ? this.stopRecording : this.requestMicrophoneAccess
          }
          disabled={isBlocked}
          data-provides="recording-wrapper"
        >
          <ConditionalWrapper
            condition={isBlocked}
            wrapper={(children) => this.disabledTooltip(children)}
          >
            <RecordButton
              isRecording={isRecording}
              disabled={isBlocked}
              data-provides="record-button"
            >
              <Label>{isRecording ? 'Stop' : 'Record'}</Label>
              <Icon
                color={themeSelector(this.props.theme?.name).icon.fillColor}
              />
            </RecordButton>
          </ConditionalWrapper>

          {isRecording ? (
            <AudioTimer onStop={this.stopRecording} />
          ) : (
            <RecordingLabel>Record</RecordingLabel>
          )}
        </Wrapper>
      );
    }
  }
}

const ConditionalWrapper = ({ condition, wrapper, children }) =>
  condition ? wrapper(children) : children;

const RecordingLabel = styled.label`
  color: ${colors.darkGrey};
  font-size: 14px;
  display: inline-block;
  vertical-align: middle;
  padding: 7px 15px;
`;

const RecordButton = styled.div`
  position: relative;
  background: ${(props) => themeSelector(props.theme?.name).buttonColor};
  ${(props) =>
    props.theme?.name === ReskinThemes.BIRCH &&
    `border: solid 1px ${PrimaryPalette.mediumEvergreen};`}
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-block;
  vertical-align: middle;
  overflow: hidden;
  box-shadow: 0 0 0 rgba(36, 164, 127, 0.4);
  will-change: box-shadow; // Performance optimization

  ${(props) => props.isRecording && PulseAnimation}

  ${Label} {
    display: none;
  }

  svg {
    display: block;
    position: relative;
    top: 50%;
    left: 50%;
    transform: translate(-49%, -51%);
    ${(props) =>
      props.isRecording &&
      css`
        transform: translate(-49%, -50%);
      `}
  }
`;
RecordButton.displayName = 'RecordButton';

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.92.0. To view the error, delete this comment and run Flow. */
const Wrapper = styled.div`
  cursor: pointer;

  ${(props) =>
    props.disabled
      ? css`
          opacity: 0.7;
          pointer-events: none;
          cursor: not-allowed;
        `
      : css`
          &:hover {
            [data-provides='record-button'] {
              background: ${themeSelector(props.theme?.name).buttonHoverColor};
            }

            & > label {
              text-decoration: underline;
            }
          }
        `}
`;
Wrapper.displayName = 'RecordingWrapper';

export default withTheme(AudioRecorder);
